import { Map } from "immutable";
import { createSelector } from "reselect";
import { descSortCompare, sortCompare } from "~/utils/utils.jsx";
import { getUserConversations } from "./selector.userConversations.jsx";
import { getLoggedUserId } from "./selector.users.jsx";

const sortMessagesByOrderId = (a, b) => {
  if (a.get("order_id") === b.get("order_id")) {
    return descSortCompare(a.get("id"), b.get("id"));
  }
  return descSortCompare(a.get("order_id"), b.get("order_id"));
};

export const getAllConversationMessages = createSelector(
  (state) => state.conversation_messages,
  (messages = Map()) => messages
);

export const getAllConversationUserMessages = createSelector(getAllConversationMessages, (messages) =>
  messages.filter((message) => message.get("class") === "ActiveCollab\\Module\\System\\Model\\Message\\UserMessage")
);

export const makeGetConversationMessageById = (id) =>
  createSelector(getAllConversationUserMessages, (messages) => {
    const message = messages.get(`${id}`);
    return message ? message.toJS() : undefined;
  });

const makeGetArrayOfConversationMessagesSelector = (conversationId) =>
  createSelector(getAllConversationMessages, (messages) => {
    return messages.filter((message) => message.get("conversation_id") === conversationId).toArray();
  });

const makeGetArrayOfConversationUserMessagesSelector = (conversationId) =>
  createSelector(getAllConversationUserMessages, (messages) => {
    return messages.filter((message) => message.get("conversation_id") === conversationId).toArray();
  });

export const makeGetConversationMessagesSelector = (conversationId) => {
  const getArrayOfConversationMessages = makeGetArrayOfConversationMessagesSelector(conversationId);
  return createSelector(getArrayOfConversationMessages, (messages) =>
    messages
      .sort((a, b) => {
        if (a.get("order_id") === b.get("order_id")) {
          return sortCompare(a.get("id"), b.get("id"));
        }
        return sortCompare(a.get("order_id"), b.get("order_id"));
      })
      .map((message) => message.toJS())
  );
};

export const makeGetRecentConversationMessageSelector = (conversationId) => {
  const getArrayOfConversationUserMessages = makeGetArrayOfConversationUserMessagesSelector(conversationId);
  return createSelector(getArrayOfConversationUserMessages, (messages) => {
    const result = messages.sort(sortMessagesByOrderId);

    return result[0] ? result[0].toJS() : undefined;
  });
};

export const getMapOfUnreadMessagesByConversationId = createSelector(
  (state) => state.conversations,
  getAllConversationUserMessages,
  getUserConversations,
  getLoggedUserId,
  (conversations, messages, userConversations, loggedUserId) => {
    return messages.toArray().reduce((acc, message) => {
      const conversationId = message.get("conversation_id");
      const createdById = message.get("created_by_id");
      const conversation = conversations.get(`${conversationId}`);
      const userConversation = userConversations.get(`${conversationId}`, Map());
      const lastVisitOn = userConversation.get("new_messages_since", 0);
      const createdOn = message.get("created_on");

      if (
        typeof conversation !== "undefined" &&
        createdById !== loggedUserId &&
        createdOn > lastVisitOn &&
        conversation.get("members", []).some((userId) => userId === loggedUserId)
      ) {
        if (!acc[conversationId]) {
          acc[conversationId] = 0;
        }

        acc[conversationId]++;
      }

      return acc;
    }, {});
  }
);

export const makeGetCountTotalUnreadMessagesByConversationSelector = (id) =>
  createSelector(getMapOfUnreadMessagesByConversationId, (mapOfCounts) => {
    return mapOfCounts[id] || 0;
  });

export const makeGetCountTotalUnreadMessagesSelector = (excludeIds) => {
  return createSelector(getMapOfUnreadMessagesByConversationId, (mapOfCounts) => {
    let result = 0;

    const ids = Object.keys(mapOfCounts).filter(
      (value) => !excludeIds.some((excludeId) => Number(value) === excludeId)
    );

    ids.forEach((id) => {
      result += mapOfCounts[id];
    });

    return result;
  });
};

export const makeGetLoggedUserLastMessageByConversationSelector = (conversationId) => {
  const getArrayOfConversationUserMessages = makeGetArrayOfConversationUserMessagesSelector(conversationId);
  return createSelector(getArrayOfConversationUserMessages, getLoggedUserId, (messages, loggedUserId) => {
    const result = messages
      .filter((message) => message.get("created_by_id") === loggedUserId)
      .sort(sortMessagesByOrderId);

    const lastMessage = result[0];

    if (typeof lastMessage !== "undefined" && !lastMessage.get("loading")) {
      return lastMessage.toJS();
    }

    return undefined;
  });
};

export const makeGetLastMessageByConversationSelector = (conversationId) => {
  const getArrayOfConversationUserMessages = makeGetArrayOfConversationUserMessagesSelector(conversationId);
  return createSelector(getArrayOfConversationUserMessages, getLoggedUserId, (messages, loggedUserId) => {
    const result = messages
      .filter((message) => message.get("created_by_id") !== loggedUserId)
      .sort(sortMessagesByOrderId);

    const lastMessage = result[0];

    if (typeof lastMessage !== "undefined" && !lastMessage.get("loading")) {
      return lastMessage.toJS();
    }

    return undefined;
  });
};
